// @ts-nocheck
import { Dialog, Transition } from "@headlessui/react";
import {
    Bars2Icon,
    BellIcon,
    CogIcon,
    HomeIcon,
    UsersIcon,
    XMarkIcon,
} from "@heroicons/react/20/solid";
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "../../components/brand/BrandLogo";
import LanguageSelector from "../../components/language/LanguageSelector";
import { useTranslation } from "../../i18n/LanguageContext";
import LogoutModal from "../login/Logout";
import axios from "axios";
import { URL } from "../../service/Service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase } from "@fortawesome/free-solid-svg-icons";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

function NotificationButton({
                                unreadCount,
                                collapsed = false,
                                active = false,
                                onClick,
                            }) {
    const { t } = useTranslation();
    const unreadLabel =
        unreadCount > 0
            ? `${unreadCount} ${
                unreadCount === 1 ? "notificação" : "notificações"
            } por ler`
            : t('nav.notifications');

    return (
        <button
            type="button"
            onClick={onClick}
            title={collapsed ? unreadLabel : t('nav.notifications')}
            className={classNames(
                "sidebar-nav-item group relative flex w-full items-center text-sm font-semibold",
                collapsed ? "justify-center px-0 py-3" : "justify-start gap-3 px-4 py-3",
                active && "sidebar-nav-item--active"
            )}
        >
      <span className="sidebar-nav-icon relative flex">
        <BellIcon
            className={classNames(
                "h-6 w-6 flex-shrink-0 transition-colors",
                active ? "text-[#18d4bd]" : "text-white/60 group-hover:text-[#18d4bd]"
            )}
            aria-hidden="true"
        />

          {unreadCount > 0 && (
              <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black leading-none text-white shadow-lg ring-2 ring-blue-green-dark">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
          )}
      </span>

            {!collapsed && (
                <div className="flex min-w-0 flex-col items-start">
                    <span className="truncate">{t('nav.notifications')}</span>

                    {unreadCount > 0 && (
                        <span
                            className={classNames(
                                "text-xs font-normal",
                                active ? "text-white/80" : "text-gray-300"
                            )}
                        >
              {unreadCount === 1 ? "1 por ler" : `${unreadCount} por ler`}
            </span>
                    )}
                </div>
            )}

            {collapsed && (
                <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block">
          {unreadLabel}
        </span>
            )}
        </button>
    );
}

function NavItem({ item, isActive, collapsed, onClick }) {
    const Icon = item.icon;

    return (
        <button
            type="button"
            onClick={onClick}
            title={collapsed ? item.name : undefined}
            className={classNames(
                "sidebar-nav-item group relative flex w-full items-center text-sm font-semibold",
                collapsed ? "justify-center px-0 py-3" : "justify-start gap-3 px-4 py-3",
                isActive && "sidebar-nav-item--active"
            )}
        >
            <span className="sidebar-nav-icon">
                <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            </span>

            {!collapsed && <span className="sidebar-nav-label truncate">{item.name}</span>}

            {collapsed && (
                <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block">
          {item.name}
        </span>
            )}
        </button>
    );
}

export default function Sb({
                               sidebarOpens,
                               setSidebarOpens,
                               opens,
                               onToggles,
                           }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [unreadCount, setUnreadCount] = useState(0);

    const navigation = useMemo(
        () => [
            {
                name: t('nav.dashboard'),
                href: "/admin/dashboard",
                icon: HomeIcon,
            },
            {
                name: t('nav.partners'),
                href: "/admin/parceiros",
                icon: UsersIcon,
            },
            {
                name: t('nav.managers'),
                href: "/admin/admins",
                icon: (props) => <FontAwesomeIcon icon={faBriefcase} {...props} />,
            },
            {
                name: t('nav.settings'),
                href: "/admin/geral",
                icon: CogIcon,
            },
        ],
        [t]
    );

    const fetchUnreadNotifications = useCallback(async () => {
        try {
            const jwtToken = sessionStorage.getItem("jwt");

            if (!jwtToken) {
                setUnreadCount(0);
                return;
            }

            const response = await axios.get(`${URL}/api/user/unread`, {
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
            });

            setUnreadCount(Number(response.data) || 0);
        } catch (error) {
            console.error("Error fetching unread notifications count:", error);
            setUnreadCount(0);
        }
    }, []);

    useEffect(() => {
        fetchUnreadNotifications();
    }, [fetchUnreadNotifications, location.pathname]);

    useEffect(() => {
        const handleRefreshNotifications = () => {
            fetchUnreadNotifications();
        };

        const handleFocus = () => {
            fetchUnreadNotifications();
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchUnreadNotifications();
            }
        };

        window.addEventListener("notifications:changed", handleRefreshNotifications);
        window.addEventListener("notifications:refresh", handleRefreshNotifications);
        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        const intervalId = window.setInterval(() => {
            fetchUnreadNotifications();
        }, 5000);

        return () => {
            window.removeEventListener(
                "notifications:changed",
                handleRefreshNotifications
            );
            window.removeEventListener(
                "notifications:refresh",
                handleRefreshNotifications
            );
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.clearInterval(intervalId);
        };
    }, [fetchUnreadNotifications]);

    const isActiveRoute = (href) => {
        return location.pathname === href || location.pathname.startsWith(`${href}/`);
    };

    const goTo = (href) => {
        navigate(href);

        if (setSidebarOpens) {
            setSidebarOpens(false);
        }

        if (href === "/admin/notifications") {
            window.setTimeout(() => {
                fetchUnreadNotifications();
            }, 300);

            window.setTimeout(() => {
                fetchUnreadNotifications();
            }, 900);

            window.setTimeout(() => {
                fetchUnreadNotifications();
            }, 1800);
        }
    };

    const notificationsActive = isActiveRoute("/admin/notifications");

    return (
        <>
            <Transition.Root show={sidebarOpens} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50 md:hidden"
                    onClose={setSidebarOpens}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-900/70" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-50 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="brand-sidebar sidebar-shell relative flex h-full w-full max-w-xs flex-col px-4 pb-5 pt-6 shadow-2xl">
                                <button
                                    type="button"
                                    onClick={() => setSidebarOpens(false)}
                                    className="absolute -right-12 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus:outline-none"
                                    title="Fechar menu"
                                >
                                    <span className="sr-only">Fechar menu</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>

                                <div className="mb-8 flex items-center">
                                    <BrandLogo />
                                </div>

                                <nav className="sidebar-nav-list flex-1 space-y-2">
                                    {navigation.map((item) => (
                                        <NavItem
                                            key={item.name}
                                            item={item}
                                            isActive={isActiveRoute(item.href)}
                                            collapsed={false}
                                            onClick={() => goTo(item.href)}
                                        />
                                    ))}
                                </nav>

                                <div className="flex justify-center pb-4">
                                    <LanguageSelector dark placement="top" />
                                </div>

                                <div className="space-y-3 border-t border-white/10 pt-4">
                                    <NotificationButton
                                        unreadCount={unreadCount}
                                        collapsed={false}
                                        active={notificationsActive}
                                        onClick={() => goTo("/admin/notifications")}
                                    />

                                    <div className="rounded-xl transition hover:bg-white/10">
                                        <LogoutModal isOpen={sidebarOpens} />
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>

                        <div className="w-14 flex-shrink-0" aria-hidden="true" />
                    </div>
                </Dialog>
            </Transition.Root>

            <aside
                className={classNames(
                    "brand-sidebar sidebar-shell hidden md:fixed md:bottom-0 md:left-0 md:top-0 md:z-40 md:flex md:h-screen md:flex-col md:border-r md:border-white/10 md:text-white/70 md:shadow-xl md:transition-all md:duration-300",
                    opens ? "md:w-64" : "md:w-20"
                )}
            >
                <div className="relative flex items-center px-4 pb-6 pt-8">
                    {opens ? (
                        <BrandLogo />
                    ) : (
                        <BrandLogo compact className="mx-auto" />
                    )}

                    <button
                        type="button"
                        onClick={onToggles}
                        className="absolute -right-4 top-24 z-50 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-blue-green-dark text-white shadow-lg transition hover:scale-105 hover:bg-green-cl focus:outline-none"
                        title={opens ? t('nav.close') : t('nav.open')}
                    >
                        <span className="sr-only">{opens ? t('nav.close') : t('nav.open')}</span>

                        {opens ? (
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                        ) : (
                            <Bars2Icon className="h-5 w-5" aria-hidden="true" />
                        )}
                    </button>
                </div>

                <nav className="sidebar-nav-list flex-1 space-y-2 px-3">
                    {navigation.map((item) => (
                        <NavItem
                            key={item.name}
                            item={item}
                            isActive={isActiveRoute(item.href)}
                            collapsed={!opens}
                            onClick={() => goTo(item.href)}
                        />
                    ))}
                </nav>

                <div className="flex justify-center px-3 pb-4">
                    <LanguageSelector dark compact={!opens} placement="top" />
                </div>

                <div className="space-y-3 border-t border-white/10 px-3 pb-5 pt-4">
                    <NotificationButton
                        unreadCount={unreadCount}
                        collapsed={!opens}
                        active={notificationsActive}
                        onClick={() => goTo("/admin/notifications")}
                    />

                    <div
                        className={classNames(
                            "rounded-xl transition hover:bg-white/10",
                            opens ? "px-2" : "flex justify-center"
                        )}
                    >
                        <LogoutModal isOpen={opens} />
                    </div>
                </div>
            </aside>

            <div className="sticky top-0 z-30 pl-2 pt-2 md:hidden">
                {!sidebarOpens && (
                    <button
                        type="button"
                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-green-dark text-white shadow-lg transition hover:bg-green-cl focus:outline-none"
                        onClick={() => setSidebarOpens(true)}
                        title={t('nav.open')}
                    >
                        <span className="sr-only">{t('nav.open')}</span>
                        <Bars2Icon className="h-6 w-6" aria-hidden="true" />
                    </button>
                )}
            </div>
        </>
    );
}
